// ── <ResponseCaptureDebugRow> — opt in to server response capture ──
//
// The per-user gate of the response capture tee (server/lib/responseCapture.js): while it
// is off, nothing this user requests is captured, even with the `chatboard-response-capture`
// flag on. On by default for sooth.inc accounts, off for any other. Rendered inside the Debug reveal only. The server
// also requires integration, the bucket and the flag, so on other deployments the toggle
// has no effect. Pure props, like PreprocessVariantDebugRow; the whole row is one
// keyboard-operable checkbox.

import { C } from "../lib/colors.js";
import { ui } from "../lib/telemetry/instrument.js";

/** The row's label, exported so a test can name it without re-typing the string. */
export const RESPONSE_CAPTURE_ROW_LABEL = "Capture responses for replay (integration)";

/**
 * @param {{value: boolean, onChange: (fn: (v: boolean) => boolean) => void,
 *          proColors: boolean}} props
 */
export default function ResponseCaptureDebugRow({ value, onChange, proColors }) {
  const on = value === true;
  const accent = proColors ? C.purple : "#33aa88";
  const toggle = () => onChange((v) => !v);
  return (
    <div
      {...ui("settings.debug-response-capture")}
      data-testid="response-capture-opt-in"
      className="settings-toggle-row"
      role="checkbox"
      tabIndex={0}
      aria-checked={on}
      aria-labelledby="response-capture-opt-in-label"
      aria-describedby="response-capture-opt-in-hint"
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          toggle();
        }
      }}
      style={{ cursor: "pointer" }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 16,
          height: 16,
          borderRadius: 3,
          border: `2px solid ${on ? accent : C.border}`,
          background: on ? accent : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s",
          flexShrink: 0,
        }}
      >
        {on && <span className="settings-toggle-check">✓</span>}
      </div>
      <div>
        <div id="response-capture-opt-in-label" className="settings-toggle-label">
          {RESPONSE_CAPTURE_ROW_LABEL}
        </div>
        <div id="response-capture-opt-in-hint" className="settings-hint">
          On by default for sooth.inc accounts, off for others. When on, the server keeps copies of
          the response bodies of an allowlist of /api routes (forecast streams and results, forecast
          lists, messages, /api/me with your email and name scrubbed) for 30 days, to replay
          sessions. Applies to the signed-in account only. Integration only.
        </div>
      </div>
    </div>
  );
}
